// Artist Performance - Main Entry Point
// Split into multiple files to avoid Babel stack overflow

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";

import PerformanceHeader from "./performance/PerformanceHeader";
import PerformanceFilters from "./performance/PerformanceFilters";
import PerformanceKPIs from "./performance/PerformanceKPIs";
import PerformanceCharts from "./performance/PerformanceCharts";
import PerformanceTables from "./performance/PerformanceTables";
import ArtistDrawer from "./performance/ArtistDrawer";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function ArtistPerformance() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canCost = user?.role === "COO";
  
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [artists, setArtists] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [trends, setTrends] = useState([]);
  
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [mtd, setMtd] = useState(true);
  const [filter, setFilter] = useState("all");
  const [mode, setMode] = useState("cost");
  const [tab, setTab] = useState("artists");
  
  const [selArt, setSelArt] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detLoading, setDetLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year, month, mtd };
      if (filter !== "all") params.work_type = filter;
      
      const results = await Promise.all([
        apiClient.get("/artist-performance/overview", { params }),
        apiClient.get("/artist-performance/by-artist", { params }),
        apiClient.get("/artist-performance/by-program", { params }),
        apiClient.get("/artist-performance/monthly-trend", { params: { months: 6 } })
      ]);
      
      setOverview(results[0].data);
      setArtists(results[1].data.artists || []);
      setPrograms(results[2].data.programs || []);
      setTrends(results[3].data.months || []);
    } catch (e) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  }, [year, month, mtd, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  
  useEffect(() => {
    if (!selArt) { setDetail(null); return; }
    setDetLoading(true);
    apiClient.get("/artist-performance/artist/" + selArt, { params: { year, month, mtd } })
      .then(r => setDetail(r.data))
      .catch(() => toast.error("Failed"))
      .finally(() => setDetLoading(false));
  }, [selArt, year, month, mtd]);

  const goPrev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const goNext = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  const chartData = artists.slice(0, 10).map(a => ({
    name: a.artist_name || "?",
    value: mode === "cost" && canCost ? (a.allocated_cost || 0) : (a.allocated_hours || 0),
    aid: a.artist_id,
    hr: a.has_rate
  }));

  const trendData = trends.map(m => ({
    name: m.month_name,
    value: mode === "cost" && canCost ? (m.allocated_cost || 0) : (m.allocated_hours || 0)
  }));

  return (
    <div className="space-y-6" data-testid="artist-performance">
      <PerformanceHeader month={month} year={year} mtd={mtd} onPrev={goPrev} onNext={goNext} MONTHS={MONTHS} />
      <PerformanceFilters mtd={mtd} setMtd={setMtd} mode={mode} setMode={setMode} filter={filter} setFilter={setFilter} canCost={canCost} />
      <PerformanceKPIs loading={loading} overview={overview} mtd={mtd} canCost={canCost} />
      <PerformanceCharts loading={loading} chartData={chartData} trendData={trendData} mode={mode} canCost={canCost} onArtistClick={setSelArt} />
      <PerformanceTables loading={loading} tab={tab} setTab={setTab} artists={artists} programs={programs} canCost={canCost} onArtistClick={setSelArt} nav={nav} />
      <ArtistDrawer open={!!selArt} onClose={() => setSelArt(null)} loading={detLoading} detail={detail} canCost={canCost} nav={nav} artId={selArt} />
    </div>
  );
}
